// FIN-3.5 — lets the project modal's single floating "Save Changes" button commit the
// finance layout as well as the project field values, so an admin never has to click
// two different saves for one edit session.
//
// FinanceSection lives three levels below the button (ProjectModalTabRenderer →
// TabManager → FinanceSection), so it publishes its edit state + save/cancel handlers
// up through this context instead of threading props through TabManager's switch.
//
// The context is OPTIONAL by design: useFinanceSaveBridge() returns null at the mount
// sites that have no floating button (the finance drawer, the sales-manager-finance
// tab), and FinanceSection falls back to rendering its own bar there. That keeps the
// layout editor saveable everywhere despite having no inline Save button of its own.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface FinanceSaveStatus {
  /** True while the finance section is in edit-layout mode. */
  active: boolean;
  /** True when the staged layout differs from its seed (edit mode only). */
  isDirty: boolean;
  isSaving: boolean;
}

interface FinanceSaveHandlers {
  save: () => Promise<void> | void;
  cancel: () => void;
}

export interface FinanceSaveBridge {
  status: FinanceSaveStatus;
  /** Called by FinanceSection on every edit-state change. `null` handlers = unmounted. */
  publish: (status: FinanceSaveStatus, handlers: FinanceSaveHandlers | null) => void;
  save: () => Promise<void>;
  cancel: () => void;
}

const IDLE: FinanceSaveStatus = { active: false, isDirty: false, isSaving: false };

const FinanceSaveBridgeContext = createContext<FinanceSaveBridge | null>(null);

export function FinanceSaveBridgeProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<FinanceSaveStatus>(IDLE);
  // Handlers live in a ref, not state: they close over the latest staged array and are
  // recreated every FinanceSection render, so storing them in state would loop.
  const handlers = useRef<FinanceSaveHandlers | null>(null);

  const publish = useCallback((next: FinanceSaveStatus, h: FinanceSaveHandlers | null) => {
    handlers.current = h;
    setStatus((prev) =>
      prev.active === next.active && prev.isDirty === next.isDirty && prev.isSaving === next.isSaving
        ? prev
        : next,
    );
  }, []);

  const save = useCallback(async () => {
    await handlers.current?.save();
  }, []);

  const cancel = useCallback(() => {
    handlers.current?.cancel();
  }, []);

  const value = useMemo<FinanceSaveBridge>(
    () => ({ status, publish, save, cancel }),
    [status, publish, save, cancel],
  );

  return <FinanceSaveBridgeContext.Provider value={value}>{children}</FinanceSaveBridgeContext.Provider>;
}

/** null when no host renders a merged save bar — the caller must provide its own. */
export function useFinanceSaveBridge(): FinanceSaveBridge | null {
  return useContext(FinanceSaveBridgeContext);
}
